if (!customElements.get('card-variant-picker')) {
  class CardVariantPicker extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-card-root]');
      if (!this.root) return;
      this.controls = this.querySelector('[data-card-controls]');
      this.selection = this.controls.querySelector('[data-option-values]').dataset.optionValues.split(',');
      this.state = { ...this.dataset };
      this.resolved = this.dataset.needsResolution !== 'true';
      this.lockedColor = this.hasAttribute('data-locked-color');
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.root.addEventListener('submit', this.onSubmit, true);
      this.syncControls();
      this.updateAddButton();

      if (this.dataset.sourceProductId) {
        this.matchData = JSON.parse(this.querySelector('[data-card-match-options]').textContent);
        if (!this.lockedColor) this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onSourceVariantChange);
        const source = document.querySelector(`product-info[data-product-id="${this.dataset.sourceProductId}"]`);
        const variant = source?.querySelector('[data-selected-variant]');
        if (variant && !this.lockedColor) this.onSourceVariantChange({ data: { sectionId: source.dataset.section, variant: JSON.parse(variant.textContent) } });
      }
      if (this.lockedColor) {
        this.lockedVariants = JSON.parse(this.querySelector('[data-locked-variants]').textContent).variants;
        if (this.dataset.needsColorResolution === 'true') this.loadLockedColor();
        return;
      }
      if (!this.resolved && this.dataset.mode === 'cart') {
        const drawer = this.closest('cart-drawer');
        const load = () => {
          if (!drawer || drawer.classList.contains('active')) {
            this.drawerObserver?.disconnect();
            this.resolveSelection();
          }
        };
        if (drawer) {
          this.drawerObserver = new MutationObserver(load);
          this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }
        load();
      } else if (!this.resolved) {
        this.resolveWhenVisible();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.drawerObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.unsubscribe?.();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.root?.removeEventListener('submit', this.onSubmit, true);
    }

    onClick = (event) => {
      const link = event.target.closest('a[data-option-id]');
      if (!link || link.hasAttribute('data-linked-product') || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.choose(link.closest('[data-option-position]').dataset.optionPosition, link.dataset.optionId);
    };

    onChange = (event) => {
      const select = event.target.closest('select');
      if (!select) return;
      const linkedProduct = select.selectedOptions[0]?.dataset.productUrl;
      if (linkedProduct) {
        window.location.assign(linkedProduct);
        return;
      }
      this.choose(select.closest('[data-option-position]').dataset.optionPosition, select.value);
    };

    onSubmit = (event) => {
      if (!event.target.closest('[data-card-add-form]')) return;
      if (this.busy || !this.resolved || this.state.available !== 'true' || !this.state.variantId) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    choose(position, id) {
      if (this.lockedColor) {
        if (Number(position) === Number(this.dataset.sizePosition)) {
          const variant = this.lockedVariants.find((candidate) => candidate.optionIds[Number(position) - 1] === id);
          if (variant) this.applyLockedVariant(variant);
        } else {
          this.dataset.manualColor = 'true';
          this.selection[Number(position) - 1] = id;
          this.syncControls();
          this.loadLockedColor(Number(position));
        }
        return;
      }
      this.interacted = true;
      this.visibilityObserver?.disconnect();
      this.drawerObserver?.disconnect();
      this.selection[Number(position) - 1] = id;
      this.syncControls();
      this.resolveSelection(Number(position));
    }

    selectionUrl() {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      if (this.lockedColor && this.resolved && this.state.variantId) {
        url.searchParams.delete('option_values');
        url.searchParams.set('variant', this.state.variantId);
        return url;
      }
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', this.selection.join(','));
      return url;
    }

    // Size changes use only the available variants rendered for this fixed color.
    matchLockedSource(selectedValues) {
      this.sourceValues = selectedValues;
      if (!this.lockedVariants?.length) return;
      const position = Number(this.dataset.sizePosition);
      const option = this.matchData?.options[position - 1];
      const matchedId = option && CardVariantPicker.matchOption(option, this.matchData, selectedValues);
      const variant = this.lockedVariants.find((candidate) => matchedId && candidate.optionIds[position - 1] === matchedId)
        || this.lockedVariants[0];
      this.applyLockedVariant(variant);
    }

    applyLockedVariant(variant) {
      this.selection = variant.optionIds;
      this.state = { variantId: variant.id, available: 'true', needsOptions: String(variant.needsOptions) };
      Object.assign(this.dataset, this.state);
      this.resolved = true;
      this.controls.querySelector('[data-option-values]').dataset.optionValues = this.selection.join(',');
      this.syncControls();
      this.updateLinks(this.selectionUrl());
      const price = this.root.querySelector('[data-card-price]');
      if (price) price.outerHTML = variant.price;
      const image = this.root.querySelector('.recommendation-card__image');
      if (image) image.innerHTML = variant.image;
      this.updateAddButton();
    }

    async loadLockedColor(focusPosition) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const requested = [...this.selection];
      const sourceValues = this.sourceValues;
      this.resolved = false;
      this.busy = true;
      this.lockedVariants = [];
      this.setAttribute('aria-busy', 'true');
      this.updateAddButton();
      this.setStatus('');
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.delete('option_values');
      url.searchParams.delete('variant');
      if (focusPosition) {
        url.searchParams.set('option_values', requested.join(','));
        this.updateLinks(url);
      } else {
        url.searchParams.set('variant', this.dataset.referenceId);
      }
      url.searchParams.set('section_id', 'complementary-variant-data');
      const timeout = setTimeout(() => controller.abort(), 8000);
      const item = this.closest('[data-complementary-key]');
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Complementary color unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(html.querySelector('[data-locked-variants]')?.textContent || 'null');
        const matchesRequest = focusPosition
          ? data?.optionIds?.join(',') === requested.join(',')
          : data?.referenceId === this.dataset.referenceId;
        if (data?.productId !== this.dataset.productId || !matchesRequest) throw new Error('Unexpected complementary variant');
        if (!this.isConnected || controller.signal.aborted || this.controller !== controller) return;
        const restoreFocus = focusPosition && this.contains(document.activeElement);
        this.lockedVariants = data.variants;
        this.querySelector('[data-locked-variants]').textContent = JSON.stringify(data);
        const options = html.querySelector('template[data-color-options]');
        if (options) this.controls.innerHTML = options.innerHTML;
        this.busy = false;
        this.dataset.needsColorResolution = 'false';
        if (!this.lockedVariants.length) {
          if (!focusPosition) return;
          this.state = { variantId: '', available: 'false', needsOptions: 'false' };
          Object.assign(this.dataset, this.state);
          this.resolved = true;
          this.root.querySelector('[data-card-price]')?.setAttribute('hidden', '');
          const image = this.root.querySelector('.recommendation-card__image');
          if (image) image.innerHTML = html.querySelector('template[data-color-image]')?.innerHTML || '';
          this.syncControls();
        } else if (focusPosition && sourceValues === this.sourceValues) {
          const position = Number(this.dataset.sizePosition) - 1;
          const variant = this.lockedVariants.find((candidate) => candidate.optionIds[position] === requested[position]) || this.lockedVariants[0];
          this.applyLockedVariant(variant);
        } else {
          this.matchLockedSource(this.sourceValues || this.closest('variant-complementary-products')?.sourceOptions || []);
        }
        item.hidden = false;
        if (restoreFocus) {
          const group = this.querySelector(`[data-option-position="${focusPosition}"]`);
          (group?.querySelector('select') || group?.querySelector(`[data-option-id="${this.selection[focusPosition - 1]}"]`))?.focus({ preventScroll: true });
        }
      } catch (error) {
        if (this.isConnected && this.controller === controller) {
          if (focusPosition) this.setStatus(window.cardVariantStrings.error);
          else if (!controller.signal.aborted) console.warn('Could not load the complementary color.', error);
        }
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) {
          this.busy = false;
          this.removeAttribute('aria-busy');
          this.updateAddButton();
          if (this.isConnected) this.dispatchEvent(new CustomEvent('complementary:resolved', { bubbles: true }));
        }
      }
    }

    async resolveSelection(focusPosition) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.busy = true;
      this.resolved = false;
      this.setAttribute('aria-busy', 'true');
      this.updateAddButton();
      this.setStatus('');
      const url = this.selectionUrl();
      this.updateLinks(url);
      url.searchParams.set('section_id', 'card-variant-options');
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Variant options unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = html.querySelector('[data-card-variant-response]');
        const options = result?.querySelector(`[data-card-options="${this.dataset.mode}"]`);
        if (result?.dataset.productId !== this.dataset.productId || !options?.querySelector('[data-option-values]')) throw new Error('Invalid variant response');
        if (!this.isConnected || this.controller !== controller) return;
        const restoreFocus = focusPosition && this.contains(document.activeElement);
        this.controls.innerHTML = options.innerHTML;
        this.selection = options.querySelector('[data-option-values]').dataset.optionValues.split(',');
        this.state = { ...result.dataset };
        this.resolved = true;
        this.syncControls();
        this.updateLinks(this.selectionUrl());
        const price = this.root.querySelector('[data-card-price]');
        const priceTemplate = this.root.classList.contains('product-card-wrapper')
          ? 'template[data-product-card-price]'
          : 'template[data-card-price]';
        if (price) price.replaceWith(result.querySelector(priceTemplate).content.cloneNode(true));
        const image = this.root.querySelector('[data-card-image]');
        const nextImage = result.querySelector('template[data-card-image]').content.querySelector('img');
        if (image && nextImage) {
          for (const name of ['src', 'srcset', 'width', 'height', 'alt']) {
            const value = nextImage.getAttribute(name);
            if (value !== null) image.setAttribute(name, value);
          }
        }
        this.root.setAttribute('data-variant-selected', '');
        if (!this.state.variantId) this.setStatus(window.variantStrings.unavailable);
        if (restoreFocus) {
          const group = this.querySelector(`[data-option-position="${focusPosition}"]`);
          (group?.querySelector('select') || group?.querySelector(`[data-option-id="${this.selection[focusPosition - 1]}"]`))?.focus({ preventScroll: true });
        }
      } catch (error) {
        if (this.isConnected && this.controller === controller) this.setStatus(window.cardVariantStrings.error);
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) {
          this.busy = false;
          this.removeAttribute('aria-busy');
          this.updateAddButton();
        }
      }
    }

    syncControls() {
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        group.querySelectorAll('[data-option-id]').forEach((link) => {
          if (link.dataset.optionId === this.selection[position - 1]) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
          if (this.lockedColor && !link.hasAttribute('data-linked-product')) {
            const values = [...this.selection];
            values[position - 1] = link.dataset.optionId;
            const url = new URL(this.dataset.productUrl, window.location.origin);
            url.searchParams.delete('variant');
            url.searchParams.set('option_values', values.join(','));
            link.href = url.pathname + url.search;
          }
        });
        const select = group.querySelector('select');
        if (select) select.value = this.selection[position - 1];
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = group.querySelector('[aria-current="true"]')?.title || label.textContent;
      });
    }

    updateLinks(url) {
      this.root.querySelectorAll('[data-card-product-link]').forEach((link) => link.setAttribute('href', url.pathname + url.search));
      this.root.querySelectorAll('[data-card-product-url]').forEach((button) => {
        button.dataset.productUrl = url.pathname + url.search;
        if (button.matches('a')) button.href = url.pathname + url.search;
      });
    }

    updateAddButton() {
      const form = this.root.querySelector('[data-card-add-form]');
      const chooser = this.root.querySelector('[data-card-choose-options]');
      const needsOptions = this.state.needsOptions === 'true';
      if (chooser) chooser.hidden = !needsOptions;
      if (!form) return;
      form.hidden = needsOptions;
      const input = form.querySelector('[name="id"]');
      const button = form.querySelector('[name="add"]');
      input.value = this.resolved ? this.state.variantId || '' : '';
      button.disabled = this.busy || !this.resolved || this.state.available !== 'true' || !this.state.variantId;
      button.querySelector('span').textContent = this.busy || !this.resolved || this.state.available === 'true' ? window.cardVariantStrings.add : this.state.variantId ? window.variantStrings.soldOut : window.variantStrings.unavailable;
    }

    setStatus(message) {
      const status = this.querySelector('[data-card-status]');
      status.textContent = message;
      status.hidden = !message;
    }

    onSourceVariantChange = ({ data }) => {
      if (this.interacted || !data.variant) return;
      const source = document.querySelector(`product-info[data-section="${CSS.escape(data.sectionId)}"]`);
      if (source?.dataset.productId !== this.dataset.sourceProductId) return;
      const next = this.matchData.options.map((option, index) => {
        const match = CardVariantPicker.matchOption(option, this.matchData, data.variant.options);
        return match || option.defaultId || this.selection[index];
      });
      if (next.join(',') === this.selection.join(',')) return;
      this.controller?.abort();
      this.controller = null;
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.selection = next;
      this.resolved = false;
      this.updateAddButton();
      this.syncControls();
      this.updateLinks(this.selectionUrl());
      this.resolveWhenVisible();
    };

    resolveWhenVisible() {
      this.visibilityObserver?.disconnect();
      if (!('IntersectionObserver' in window)) return this.resolveSelection();
      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        this.visibilityObserver.disconnect();
        this.resolveSelection();
      }, { rootMargin: '800px 0px' });
      this.visibilityObserver.observe(this.root);
    }

    static matchOption(option, source, selectedValues) {
      const normalize = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
      const names = window.cardVariantStrings;
      const colorNames = (names.colorNames || '').split(',').map(normalize);
      const sizeNames = (names.sizeNames || '').split(',').map(normalize);
      const role = colorNames.includes(normalize(option.name)) ? 'color' : sizeNames.includes(normalize(option.name)) ? 'size' : '';
      if (!role) return '';
      const acceptedNames = role === 'color' ? colorNames : sizeNames;
      const sourceIndex = source.sourceNames.findIndex((name) => acceptedNames.includes(normalize(name)));
      const value = normalize(selectedValues[sourceIndex]);
      if (!value) return '';
      if (role === 'size' && !'xxs,xs,s,m,l,xl,xxl,xxxl,2xl,3xl,4xl,5xl,6xl'.split(',').includes(value) && (!source.category || source.category !== source.sourceCategory)) return '';
      let matches = option.values.filter((candidate) => normalize(candidate.name) === value);
      if (!matches.length && role === 'color') {
        const aliases = (names.colorAliases || '').split('\n').map((line) => line.split('=').map(normalize)).find((group) => group.includes(value)) || [];
        matches = option.values.filter((candidate) => aliases.includes(normalize(candidate.name)));
      }
      return matches.length === 1 ? matches[0].id : '';
    }
  }
  customElements.define('card-variant-picker', CardVariantPicker);
}
